import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { Calendar, User, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // First get all posts and find by slug
        const response = await fetch(`${API_URL}/api/blog`);
        if (response.ok) {
          const posts = await response.json();
          const foundPost = posts.find(p => p.slug === slug);
          if (foundPost) {
            setPost(foundPost);
          } else {
            setError('Post not found');
          }
        }
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const getTranslation = (post) => {
    const lang = getUiLang(i18n);
    return post.translations[lang] || post.translations['sk'] || post.translations['en'];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: getTranslation(post).title,
          url: window.location.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t('blog.link_copied'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold mb-4">{t('blog.post_not_found')}</h1>
        <Link to="/blog">
          <Button>{t('blog.back_to_blog')}</Button>
        </Link>
      </div>
    );
  }

  const translation = getTranslation(post);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image */}
      <div className="relative h-[400px] bg-gray-900">
        <img
          src={post.images[0] || 'https://via.placeholder.com/1200x400'}
          alt={translation.title}
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-white/80 hover:text-white mb-4 transition"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('blog.back_to_blog')}
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="blog-post-title">
              {translation.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {post.author}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            {/* Share Button */}
            <div className="flex justify-end mb-6">
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-2" />
                {t('blog.share')}
              </Button>
            </div>

            {/* Article Content */}
            <article className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-orange-500">
              <div 
                className="whitespace-pre-wrap text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ 
                  __html: translation.content
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n\n/g, '</p><p class="mb-4">')
                    .replace(/^/, '<p class="mb-4">')
                    .replace(/$/, '</p>')
                }}
              />
            </article>

            {/* Tags/Category */}
            <div className="mt-8 pt-8 border-t">
              <span className="inline-block px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm">
                {post.category}
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 bg-gradient-to-r from-orange-500 to-yellow-400 rounded-xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">{t('blog.cta_title')}</h3>
            <p className="mb-6 opacity-90">{t('blog.cta_text')}</p>
            <Link to="/contact">
              <Button className="bg-white text-orange-500 hover:bg-gray-100">
                {t('nav.contact')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
